import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios, { API } from '../services/api';
import { toast } from 'sonner';
import {
  Heart, Users, SignOut, CaretRight, ArrowLeft, Globe, Check,
  Info, ChartBar, ShieldCheck, UserGear, ArrowCounterClockwise,
  FilePdf, DownloadSimple, Spinner, Lock, EnvelopeSimple, Question, Lifebuoy
} from '@phosphor-icons/react';
import ReferralCard from '../components/billing/ReferralCard';
import { CompactFooter } from '../components/shared/CompactFooter';

const COMMON_TIMEZONES = [
  'Pacific/Auckland', 'Australia/Sydney', 'Australia/Adelaide', 'Australia/Perth',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Asia/Dubai',
  'Europe/Moscow', 'Europe/Istanbul', 'Europe/Berlin', 'Europe/London',
  'Atlantic/Reykjavik', 'America/Sao_Paulo', 'America/New_York',
  'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Pacific/Honolulu',
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [timezone, setTimezone] = useState('');
  const [showTzPicker, setShowTzPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [exporting, setExporting] = useState(null); // 'pdf' | 'json' | null
  const [digestOptIn, setDigestOptIn] = useState(true);
  const [digestSaving, setDigestSaving] = useState(false);
  // Stage 2-P2 — Founder Member badge data.
  const [billingStatus, setBillingStatus] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { data } = await axios.get(`${API}/profile`);
        setTimezone(data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      } catch { /* ignore */ }
      try {
        const { data } = await axios.get(`${API}/profile/preferences`);
        setDigestOptIn(data.weekly_digest_opt_in !== false);
      } catch { /* ignore */ }
      try {
        const { data } = await axios.get(`${API}/billing/status`);
        setBillingStatus(data);
      } catch { /* ignore */ }
    };
    loadProfile();
  }, []);

  const toggleDigest = async () => {
    const next = !digestOptIn;
    setDigestSaving(true);
    setDigestOptIn(next); // optimistic
    try {
      await axios.put(`${API}/profile/preferences`, { weekly_digest_opt_in: next });
      toast.success(next ? 'Weekly digest turned on' : 'Weekly digest paused');
    } catch {
      setDigestOptIn(!next); // rollback
      toast.error('Could not save preference');
    } finally {
      setDigestSaving(false);
    }
  };

  const updateTimezone = async (tz) => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/timezone`, { timezone: tz });
      setTimezone(tz);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      setShowTzPicker(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleExportPDF = async () => {
    setExporting('pdf');
    try {
      const response = await axios.get(`${API}/export/doctor-summary`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Kindra_Health_Summary_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Health summary downloaded');
    } catch {
      toast.error('Failed to generate summary. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  const handleExportJSON = async () => {
    setExporting('json');
    try {
      const { data } = await axios.get(`${API}/export/health-data`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Kindra_Health_Export_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Health data exported');
    } catch {
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <header className="glass-header sticky top-0 z-10 px-4 py-3 flex items-center gap-4 min-h-[56px]">
        <button onClick={() => navigate('/home')} className="text-slate-600 hover:text-slate-900 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center -ml-2" data-testid="profile-back-btn">
          <ArrowLeft size={24} />
        </button>
        <h1 className="font-medium text-slate-900">Profile</h1>
      </header>

      <div className="flex-1 px-6 py-6">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 gradient-bg rounded-full flex items-center justify-center">
            <span className="text-2xl font-medium text-white">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <h2 className="text-xl font-medium text-slate-900">{user?.name}</h2>
          <p className="text-slate-500">{user?.email}</p>
          {/* Stage 2-P2 — Founder Member badge */}
          {billingStatus?.is_founder_member && (
            <div
              data-testid="founder-member-badge"
              className="mt-3 inline-flex items-center gap-1.5 bg-[#0E1C2E] text-white px-3 py-1 rounded-full text-[11px] font-medium tracking-wider uppercase"
              title={billingStatus.founder_since ? `Founder member since ${(billingStatus.founder_since || '').slice(0, 10)}` : 'Founder member'}
            >
              <span className="text-[#CDA873]">✦</span>
              <span>Founder Member</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          {/* Timezone Setting */}
          <div className="card-base p-4" data-testid="timezone-setting">
            <button
              onClick={() => setShowTzPicker(!showTzPicker)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <Globe size={20} className="text-[#235347]" />
                <div className="text-left">
                  <span className="text-slate-700 block">Timezone</span>
                  <span className="text-xs text-slate-500">{timezone || 'Not set'}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {saved && <Check size={16} className="text-green-500" />}
                <CaretRight size={20} className={`text-slate-400 transition-transform ${showTzPicker ? 'rotate-90' : ''}`} />
              </div>
            </button>

            {showTzPicker && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                {detectedTz !== timezone && (
                  <button
                    onClick={() => updateTimezone(detectedTz)}
                    className="w-full mb-3 p-3 bg-[#235347]/5 border border-[#235347]/20 rounded-xl text-left hover:bg-[#235347]/10 transition-colors"
                    data-testid="use-detected-tz-btn"
                  >
                    <p className="text-sm font-medium text-[#235347]">Use detected timezone</p>
                    <p className="text-xs text-slate-500">{detectedTz}</p>
                  </button>
                )}
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {COMMON_TIMEZONES.map(tz => (
                    <button
                      key={tz}
                      onClick={() => updateTimezone(tz)}
                      disabled={saving}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        timezone === tz ? 'bg-[#235347] text-white' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                      data-testid={`tz-option-${tz}`}
                    >
                      {tz.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Weekly digest toggle */}
          <div className="card-base p-4" data-testid="digest-setting">
            <button
              onClick={toggleDigest}
              disabled={digestSaving}
              className="w-full flex items-center justify-between disabled:opacity-60"
              data-testid="digest-toggle-btn"
            >
              <div className="flex items-center gap-3">
                <EnvelopeSimple size={20} className="text-[#235347]" />
                <div className="text-left">
                  <span className="text-slate-700 block">Weekly digest</span>
                  <span className="text-xs text-slate-500">Sunday 7pm — what changed in your health</span>
                </div>
              </div>
              <span
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${digestOptIn ? 'bg-[#235347]' : 'bg-slate-300'}`}
                aria-pressed={digestOptIn}
                data-testid="digest-toggle-switch"
                data-state={digestOptIn ? 'on' : 'off'}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${digestOptIn ? 'translate-x-5' : 'translate-x-0.5'}`}
                />
              </span>
            </button>
          </div>

          {/* Stage 11 — Referral Engine: calm "invite a friend" card. */}
          <ReferralCard />

          <button
            onClick={() => navigate('/health')}
            className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            data-testid="my-health-data-btn"
          >
            <div className="flex items-center gap-3">
              <Heart size={20} className="text-[#235347]" />
              <span className="text-slate-700">My Health Data</span>
            </div>
            <CaretRight size={20} className="text-slate-400" />
          </button>

          <button
            onClick={() => navigate('/contacts')}
            className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            data-testid="support-network-btn"
          >
            <div className="flex items-center gap-3">
              <Users size={20} className="text-[#235347]" />
              <span className="text-slate-700">Support Network</span>
            </div>
            <CaretRight size={20} className="text-slate-400" />
          </button>

          <button
            onClick={() => navigate('/billing')}
            className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            data-testid="billing-btn"
          >
            <div className="flex items-center gap-3">
              <ShieldCheck size={20} className="text-[#235347]" />
              <span className="text-slate-700">Billing & Subscription</span>
            </div>
            <CaretRight size={20} className="text-slate-400" />
          </button>

          {user?.role === 'admin' && (
            <>
              <button
                onClick={() => navigate('/founder/growth')}
                className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                data-testid="founder-growth-btn"
              >
                <div className="flex items-center gap-3">
                  <ChartBar size={20} className="text-[#2FB3A0]" />
                  <span className="text-slate-700">Growth & Proof</span>
                </div>
                <CaretRight size={20} className="text-slate-400" />
              </button>
              <button
                onClick={() => navigate('/founder')}
                className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                data-testid="founder-panel-btn"
              >
                <div className="flex items-center gap-3">
                  <ChartBar size={20} className="text-[#235347]" />
                  <span className="text-slate-700">Founder Panel</span>
                </div>
                <CaretRight size={20} className="text-slate-400" />
              </button>
            </>
          )}

          {/* Stage 20B+ Wave A — Get help entry point. Visible inside the
              app so users never feel they have to guess where to ask. */}
          <button
            data-testid="get-help-btn"
            onClick={() => navigate('/contact')}
            className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Lifebuoy size={20} className="text-[#2FB3A0]" weight="duotone" />
              <div className="text-left">
                <span className="text-slate-700 block">Get help</span>
                <span className="text-xs text-slate-400">Talk to a real person — we reply in a business day</span>
              </div>
            </div>
            <CaretRight size={20} className="text-slate-400" />
          </button>

          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full card-base p-4 flex items-center gap-3 text-red-600 hover:bg-red-50 transition-colors"
          >
            <SignOut size={20} />
            <span>Sign Out</span>
          </button>

          {/* Account Settings */}
          <div className="pt-4 border-t border-slate-100 mt-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 px-1">Account</p>
            <button
              onClick={() => navigate('/change-password')}
              className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors mb-2"
              data-testid="change-password-btn"
            >
              <div className="flex items-center gap-3">
                <Lock size={20} className="text-slate-500" />
                <div className="text-left">
                  <span className="text-slate-700 block">Change Password</span>
                  <span className="text-xs text-slate-400">Update your sign-in password</span>
                </div>
              </div>
              <CaretRight size={20} className="text-slate-400" />
            </button>
            <button
              onClick={async () => {
                if (!window.confirm('This will restart the onboarding setup. Your existing health data will not be deleted.')) return;
                try {
                  await axios.post(`${API}/onboarding/reset`);
                  navigate('/onboarding');
                } catch (err) { console.error(err); }
              }}
              className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
              data-testid="rerun-onboarding-btn"
            >
              <div className="flex items-center gap-3">
                <ArrowCounterClockwise size={20} className="text-slate-500" />
                <div className="text-left">
                  <span className="text-slate-700 block">Re-run Setup</span>
                  <span className="text-xs text-slate-400">Redo your health profile onboarding</span>
                </div>
              </div>
              <CaretRight size={20} className="text-slate-400" />
            </button>
          </div>

          {/* Export & Sharing */}
          <div className="pt-4 border-t border-slate-100 mt-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 px-1">Export</p>
            <div className="space-y-2">
              <button
                onClick={handleExportPDF}
                disabled={!!exporting}
                className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors disabled:opacity-50"
                data-testid="profile-export-pdf"
              >
                <div className="flex items-center gap-3">
                  {exporting === 'pdf' ? <Spinner size={20} className="text-red-400 animate-spin" /> : <FilePdf size={20} className="text-red-400" weight="duotone" />}
                  <div className="text-left">
                    <span className="text-slate-700 block">Doctor Summary</span>
                    <span className="text-xs text-slate-400">Printable PDF for appointments</span>
                  </div>
                </div>
                <CaretRight size={20} className="text-slate-400" />
              </button>
              <button
                onClick={handleExportJSON}
                disabled={!!exporting}
                className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors disabled:opacity-50"
                data-testid="profile-export-json"
              >
                <div className="flex items-center gap-3">
                  {exporting === 'json' ? <Spinner size={20} className="text-blue-400 animate-spin" /> : <DownloadSimple size={20} className="text-blue-400" weight="duotone" />}
                  <div className="text-left">
                    <span className="text-slate-700 block">Export Health Data</span>
                    <span className="text-xs text-slate-400">Structured JSON backup</span>
                  </div>
                </div>
                <CaretRight size={20} className="text-slate-400" />
              </button>
            </div>
          </div>

          {/* Info & Legal */}
          <div className="pt-4 border-t border-slate-100 mt-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 px-1">Info & Legal</p>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/about')}
                className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                data-testid="about-link"
              >
                <div className="flex items-center gap-3">
                  <Info size={20} className="text-slate-500" />
                  <span className="text-slate-700">About Kindra</span>
                </div>
                <CaretRight size={20} className="text-slate-400" />
              </button>
              <button
                onClick={() => navigate('/investor')}
                className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                data-testid="investor-link"
              >
                <div className="flex items-center gap-3">
                  <ChartBar size={20} className="text-slate-500" />
                  <span className="text-slate-700">Investor</span>
                </div>
                <CaretRight size={20} className="text-slate-400" />
              </button>
              <button
                onClick={() => navigate('/confidentiality')}
                className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                data-testid="nda-link"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheck size={20} className="text-slate-500" />
                  <span className="text-slate-700">Confidentiality Agreement</span>
                </div>
                <CaretRight size={20} className="text-slate-400" />
              </button>
            </div>
          </div>

          {/* Admin Section */}
          {user?.role === 'admin' && (
            <div className="pt-4 border-t border-slate-100 mt-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 px-1">Admin</p>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/admin/invites')}
                  className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  data-testid="admin-invites-link"
                >
                  <div className="flex items-center gap-3">
                    <UserGear size={20} className="text-[#235347]" />
                    <span className="text-slate-700">Manage Invites</span>
                  </div>
                  <CaretRight size={20} className="text-slate-400" />
                </button>
                <button
                  onClick={() => navigate('/admin/agreements')}
                  className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  data-testid="admin-nda-register-link"
                >
                  <div className="flex items-center gap-3">
                    <ShieldCheck size={20} className="text-[#235347]" />
                    <span className="text-slate-700">NDA Register</span>
                  </div>
                  <CaretRight size={20} className="text-slate-400" />
                </button>
                <button
                  onClick={() => navigate('/admin/users')}
                  className="w-full card-base p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  data-testid="admin-users-link"
                >
                  <div className="flex items-center gap-3">
                    <Users size={20} className="text-[#235347]" />
                    <span className="text-slate-700">Manage Users</span>
                  </div>
                  <CaretRight size={20} className="text-slate-400" />
                </button>
              </div>
            </div>
          )}
        </div>
        <CompactFooter />
      </div>
    </div>
  );
};

export default ProfilePage;
